// api.ts — the objectifio REST surface.
//
// Two pages talk to five endpoints. There is deliberately no upload call here:
// the corpus is administered out-of-band and the browser app has no way to add
// to it.
import { createApiClient } from 'common'

// The session is an HttpOnly cookie the server sets after the OIDC round-trip,
// so the token slot stays empty in practice — the client is here because the
// shared useAuth hook takes one, and because a Bearer token is still accepted
// if someone drops one in.
export const apiClient = createApiClient({ tokenKey: 'objectifio_token' })

export interface SubjectBrief {
    id: string
    imageUrl: string
    sourceUrl: string
    license: string
}

export interface NextPair {
    left: SubjectBrief
    right: SubjectBrief
    /** Expected mutual information of the answer, in nats. */
    infoGain: number
    /** True when the pair was drawn for coverage rather than by information gain. */
    exploring: boolean
    judged: number
    corpusSize: number
}

/** +1 left, 0 equally attractive, -1 right. */
export type Outcome = 1 | 0 | -1

export interface RankedSubject {
    id: string
    imageUrl: string
    score: number
    /** Posterior standard deviation of the score — the reason this is a
     *  stochastic ordering rather than a ranking. */
    sd: number
    seen: number
}

export interface Reveal {
    email: string
    judged: number
    vector: number[]
    sd: number[]
    tieThreshold: number
    ordering: RankedSubject[]
    /** True when too few judgements have been made for any of it to mean much. */
    cold: boolean
    coldBelow: number
}

export interface AxisView {
    rank: number
    varianceFrac: number
    singularValue: number
    nUsers: number
    label: string | null
    description: string | null
    highImages: string[]
    lowImages: string[]
    /** Where the viewer's own preference vector falls on this axis. */
    projection: number
}

export interface Axes {
    ready: boolean
    raters: number
    minRaters: number
    axes: AxisView[]
}

export class ApiError extends Error {
    constructor(message: string, readonly status: number) {
        super(message)
        this.name = 'ApiError'
    }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(path, { credentials: 'include', ...init })
    if (!res.ok) {
        let message = `${res.status} ${res.statusText}`
        try {
            const body = await res.json()
            if (body?.error) message = body.error
        } catch {
            // A non-JSON error body is still an error; the status line will do.
        }
        throw new ApiError(message, res.status)
    }
    return res.json() as Promise<T>
}

/** Ask for the most informative comparison this rater could answer next. */
export function getNextPair(): Promise<NextPair> {
    return request<NextPair>('/api/pair')
}

/** Record one judgement. */
export function submitComparison(
    leftId: string,
    rightId: string,
    outcome: Outcome,
    latencyMs: number,
): Promise<{ id: number; judged: number }> {
    return request('/api/comparisons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leftId, rightId, outcome, latencyMs }),
    })
}

/** The caller's own embedding and ordering. Never anyone else's. */
export function getReveal(limit = 60): Promise<Reveal> {
    return request<Reveal>(`/api/reveal?limit=${limit}`)
}

/** The population's principal preference directions. */
export function getAxes(): Promise<Axes> {
    return request<Axes>('/api/axes')
}
